<template>
  <div data-testId="studio-code-editor">
    <div class="tw-relative">
      <div
        class="tw-absolute tw-right-0 tw-top-[-18px] tw-mr-1 tw-text-xs"
        :class="[characterLimit > 0 && charactersUsed > characterLimit ? 'tw-text-red-500' : 'tw-text-gray-500']"
      >
        <span data-testId="characters-used">{{ charactersUsed }}</span>
        <span v-if="characterLimit > 0" data-testId="character-limit"> / {{ characterLimit }}</span>
        <span> Characters</span>
      </div>
    </div>
    <div :id="cmContainerId"></div>
  </div>
</template>

<script lang="ts">
import { Extension, EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { defineComponent, PropType, markRaw } from 'vue';

import { languageExtension } from '@/studio/marketingTags/marketingTags.customExtensions';
import { basicSetup } from '@/studio/marketingTags/marketingTags.utils';
import { Language } from '@/studio/marketingTags/store/types';

export default defineComponent({
  name: 'StudioCodeEditor',
  props: {
    cmContainerId: {
      type: String,
      default: 'code-mirror-container'
    },
    doc: {
      type: String,
      default: ''
    },
    selection: {
      type: Object as PropType<{ anchor: number; head: number }>,
      default: () => ({ anchor: 0, head: 0 })
    },
    language: {
      type: String as PropType<Language>,
      default: undefined
    },
    addedExtensions: {
      type: Array as () => Extension[],
      default: () => []
    },
    useBasicSetup: {
      type: Boolean,
      default: true
    },
    characterLimit: {
      type: Number,
      default: 0
    },
    /** Non-standard HTML attributes to offer in autocomplete, as `{ name: [...values] }`. */
    extraHtmlAttributes: {
      type: Object as PropType<Record<string, readonly string[]>>,
      default: undefined
    }
  },
  data() {
    return {
      view: {} as EditorView,
      charactersUsed: 0
    };
  },
  mounted() {
    this.initializeCodemirror();
  },
  methods: {
    createNewState(): EditorState {
      const newState = EditorState.create({
        doc: this.doc,
        selection: this.selection,
        extensions: [
          this.addedExtensions,
          this.language ? languageExtension(this.language, this.extraHtmlAttributes) : [],
          this.useBasicSetup ? basicSetup : [],
          this.onChangeListener()
        ]
      });
      this.$emit('stateCreated', newState);
      return newState;
    },
    initializeCodemirror() {
      const codeEditorParent = document.getElementById(this.cmContainerId) || undefined;
      this.view = markRaw(
        new EditorView({
          state: this.createNewState(),
          parent: codeEditorParent
        })
      );
      this.charactersUsed = this.view.state.doc.toString().length;
      this.$emit('viewCreated', this.view);
    },
    onChangeListener() {
      return EditorView.updateListener.of(update => {
        if (update.docChanged) {
          this.charactersUsed = this.view.state.doc.toString().length;
          this.onChange(update.state.doc.toString());
        }
      });
    },
    onChange(doc: string) {
      this.$emit('change', doc);
    }
  }
});
</script>
